import { useMemo, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  clockOn,
  formatHoldMinutes,
  holdChoices,
  holdFeeCents,
  hourMinutes,
  stayDays,
  stayEnd,
  stayTotalCents,
  type MapSpot,
  type MapVenue,
  type ReservationRequest,
} from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { DateField, TimeField } from '@/components/picker-field';
import { Field, INPUT } from '@/components/field';
import { NOT_A_CONTACT, STAFF_FIELD_NAMES } from '@/lib/autofill';
import { moneyOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// The zones of the spots picked, so one selection is priced as one hold. §9
export function ratesOf(venue: MapVenue, spots: readonly MapSpot[]): number[] {
  return spots.map(
    (spot) => venue.zones.find((zone) => zone.key === spot.zoneKey)?.holdPerHourCents ?? 0,
  );
}

export function dayPricesOf(venue: MapVenue, spots: readonly MapSpot[]): number[] {
  return spots.map(
    (spot) => venue.zones.find((zone) => zone.key === spot.zoneKey)?.dayPriceCents ?? 0,
  );
}

// One guest, one range, one hold, however many spots are selected (§10) —
// the fields alone, so the same form serves the bulk dialog and the one a
// single spot opens with its status actions underneath.
export function ReserveForm({
  venue,
  spots,
  date,
  today,
  nowMinutes,
  busy,
  refused,
  children,
  className,
  dialogLabel,
  nameSpots,
  drop,
  onSubmit,
  onCancel,
}: {
  venue: MapVenue;
  spots: readonly MapSpot[];
  date: string;
  today: string;
  nowMinutes: number;
  busy: boolean;
  // §10: a refused booking keeps this screen, and everything typed on it.
  refused?: string | null;
  children?: ReactNode;
  className?: string;
  dialogLabel?: string;
  nameSpots?: boolean;
  // §10: a refused selection is retried without the spot that refused it.
  drop?: { readonly label: string; readonly onDrop: (spot: MapSpot) => void };
  onSubmit: (request: ReservationRequest) => void;
  onCancel?: () => void;
}): ReactElement {
  const { t, language } = useTranslation();
  const [startsOn, setStartsOn] = useState(date);
  const [endsOn, setEndsOn] = useState(date);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [notes, setNotes] = useState('');
  const [picked, setPicked] = useState<number | null>(null);

  const rates = useMemo(() => ratesOf(venue, spots), [venue, spots]);
  // Only a stay starting today answers to the clock; §9 leaves later days alone.
  const clock = clockOn(startsOn, today, nowMinutes);
  const choices = useMemo(() => holdChoices(rates, venue.hold, clock), [rates, venue.hold, clock]);
  // A deadline that has since gone by is not held on to: the list decides.
  const holdMinutes = choices.find((c) => c.minutes === picked)?.minutes ?? choices[0]?.minutes;
  // Nothing left to hold: the guest is seated on arrival instead of held. §10
  const arriving = holdMinutes === undefined;
  const fee = arriving ? 0 : holdFeeCents(rates, holdMinutes, venue.hold, clock);
  // A range typed backwards is one day, not an error message. §10
  const lastDay = stayEnd(startsOn, endsOn);

  // The To follows the From past it, so the field never reads backwards. §10
  function moveStart(next: string): void {
    setStartsOn(next);
    setEndsOn(stayEnd(next, endsOn));
  }

  const days = stayDays(startsOn, lastDay);
  const total = stayTotalCents(dayPricesOf(venue, spots), days, fee);
  const named = guestName.trim().length > 0;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!named) return;
    onSubmit({
      spotIds: spots.map((spot) => spot.id),
      guestName: guestName.trim(),
      guestPhone: guestPhone.trim() === '' ? null : guestPhone.trim(),
      guestEmail: guestEmail.trim() === '' ? null : guestEmail.trim(),
      startsOn,
      endsOn: lastDay,
      holdMinutes: holdMinutes ?? null,
      arrivalTime: arrivalTime === '' ? null : arrivalTime,
      // §10's deposit is recorded, not typed: the field is deferred (TODOS).
      depositCents: 0,
      notes: notes.trim() === '' ? null : notes.trim(),
    });
  }

  return (
    <form
      role={dialogLabel === undefined ? undefined : 'dialog'}
      aria-label={dialogLabel}
      onSubmit={submit}
      className={cn('flex min-h-0 flex-col', className)}
    >
      {dialogLabel !== undefined && (
        <h3 className="mx-auto w-full max-w-[560px] shrink-0 pb-2.5 text-[15px] font-bold">
          {dialogLabel}
        </h3>
      )}
      {/* The frame holds still; only the fields move under a finger. §20 */}
      <div className="mx-auto flex w-full max-w-[560px] min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto overscroll-contain px-1">
        {/* §10: a spot the server refused comes off here, and the rest go. */}
        {nameSpots === true && (
          <p className="flex flex-wrap gap-1.5" data-testid="reserve-spots">
            {spots.map((spot) => (
              <span
                key={spot.id}
                className="inline-flex items-center gap-1 rounded-lg bg-secondary py-1 ps-2 pe-1 text-xs font-bold"
              >
                #{spot.number}
                {drop !== undefined && spots.length > 1 && (
                  <button
                    type="button"
                    aria-label={`${drop.label} #${spot.number}`}
                    className="grid size-5 place-items-center rounded-md hover:bg-background"
                    onClick={() => drop.onDrop(spot)}
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                )}
              </span>
            ))}
          </p>
        )}

        {/* One row at every width: the fields are sized by our own text. §20 */}
        <div className="grid grid-cols-2 gap-2">
          <DateField label={t('from')} value={startsOn} min={date} onPick={moveStart} />
          <DateField label={t('to')} value={endsOn} min={startsOn} onPick={setEndsOn} />
        </div>

        {arriving ? (
          <p className="rounded-xl border px-3 py-2 text-[13px] text-muted-foreground">
            {t('holdWindowClosed')} · {formatHoldMinutes(hourMinutes(venue.hold.latestHour))}
          </p>
        ) : (
          <Field label={t('heldUntil')}>
            <select
              className={INPUT}
              value={holdMinutes}
              onChange={(event) => setPicked(Number(event.target.value))}
            >
              {choices.map((choice) => (
                <option key={choice.minutes} value={choice.minutes}>
                  {formatHoldMinutes(choice.minutes)} ·{' '}
                  {choice.feeCents === 0
                    ? t('holdIncluded')
                    : `+${moneyOf(choice.feeCents, venue.currency, language)}`}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* The guest's details are not the staff member's own. §10, lib/autofill */}
        <Field label={`${t('guest')} *`}>
          <input
            className={INPUT}
            name={STAFF_FIELD_NAMES.guest}
            {...NOT_A_CONTACT}
            value={guestName}
            required
            onChange={(event) => setGuestName(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label={t('phone')}>
            <input
              className={INPUT}
              name={STAFF_FIELD_NAMES.phone}
              {...NOT_A_CONTACT}
              value={guestPhone}
              onChange={(event) => setGuestPhone(event.target.value)}
            />
          </Field>
          <TimeField label={t('arrival')} value={arrivalTime} onPick={setArrivalTime} />
        </div>

        <Field label={t('email')}>
          <input
            type="email"
            className={INPUT}
            name={STAFF_FIELD_NAMES.email}
            {...NOT_A_CONTACT}
            value={guestEmail}
            onChange={(event) => setGuestEmail(event.target.value)}
          />
        </Field>

        <Field label={t('notes')}>
          <input
            className={INPUT}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        <p className="rounded-xl border px-3 py-2 text-[13px]" data-testid="reserve-total">
          <span className="text-muted-foreground">{t('total')}</span>{' '}
          <b>{moneyOf(total, venue.currency, language)}</b>{' '}
          <span className="text-muted-foreground">
            · {spots.length} × {days} {t('daysWord')}
            {fee > 0 && ` · ${t('holdFee')} ${moneyOf(fee, venue.currency, language)}`}
          </span>
        </p>
        {children}
      </div>

      {refused !== null && refused !== undefined && (
        <p
          role="alert"
          className="mx-auto mt-2.5 w-full max-w-[560px] shrink-0 rounded-xl bg-red-100 px-3 py-2 text-[13px] text-red-800"
        >
          {refused}
        </p>
      )}

      {/* One row, two buttons of one height and one type size. §20 */}
      <div className="mx-auto flex w-full max-w-[560px] shrink-0 items-stretch gap-2 pt-2.5">
        {onCancel !== undefined && (
          <Button
            type="button"
            variant="outline"
            className="h-12 flex-1 text-[15px]"
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
        )}
        <Button type="submit" className="h-12 flex-1 text-[15px]" disabled={busy || !named}>
          {t(arriving ? 'reserveAndCheckIn' : 'saveReservation')}
        </Button>
      </div>
    </form>
  );
}
